import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Icon } from "./Icon";

/** Tag — inline metadata label. `type` = mono data descriptor, `hosp` = capability. */
export function Tag({
  variant = "neutral",
  icon,
  className,
  children,
}: {
  variant?: "neutral" | "type" | "hosp";
  icon?: LucideIcon;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "tag",
        variant === "type" && "tag--type",
        variant === "hosp" && "tag--hosp",
        className,
      )}
    >
      {icon && <Icon icon={icon} size={11} />}
      {children}
    </span>
  );
}

/** The five canonical workflow states. Every rider/job status maps to one. */
export type RiderStatus =
  | "pending"
  | "processing"
  | "extracted"
  | "approved"
  | "failed";

const STATUS: Record<RiderStatus, { cls: string; label: string }> = {
  pending: { cls: "st-pending", label: "Pending" },
  processing: { cls: "st-processing", label: "Processing" },
  extracted: { cls: "st-extracted", label: "Extracted" },
  approved: { cls: "st-approved", label: "Approved" },
  failed: { cls: "st-failed", label: "Failed" },
};

export function StatusBadge({
  status,
  label,
  className,
}: {
  status: RiderStatus;
  label?: ReactNode;
  className?: string;
}) {
  const s = STATUS[status] ?? STATUS.pending;
  return (
    <span className={cn("status-badge", s.cls, className)}>
      <i />
      {label ?? s.label}
    </span>
  );
}

/** Chip — compact list token. `crit` elevates a hard constraint. */
export function Chip({
  crit,
  className,
  children,
}: {
  crit?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return <span className={cn("chip", crit && "chip--crit", className)}>{children}</span>;
}
